import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LockClosedIcon,
  KeyIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3402';

const SecuritySettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwords, setPasswords] = useState({
    current: '',
    new: '',
    confirm: '',
  });

  useEffect(() => {
    fetchSecurityStatus();
  }, []);

  const fetchSecurityStatus = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/users/security-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setTwoFactorEnabled(data.twoFactorEnabled || false);
      }
    } catch (error) {
      console.error('Failed to load security status:', error);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwords.new !== passwords.confirm) {
      alert('New passwords do not match');
      return;
    }

    setLoading(true);
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const res = await fetch(`\${API_BASE_URL}/api/users/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwords.current,
          newPassword: passwords.new,
        }),
      });

      if (res.ok) {
        setSuccess(true);
        setShowPasswordForm(false);
        setPasswords({ current: '', new: '', confirm: '' });
        setTimeout(() => setSuccess(false), 3000);
      } else {
        alert('Failed to change password');
      }
    } catch (error) {
      console.error('Failed to change password:', error);
      alert('Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle2FA = async () => {
    if (twoFactorEnabled) {
      navigate('/dashboard/settings/two-factor/disable');
    } else {
      navigate('/dashboard/settings/two-factor/setup');
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard/settings')}
          className="text-sm text-indigo-600 hover:text-indigo-500 mb-4"
        >
          ← Back to Settings
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Security Settings</h1>
        <p className="mt-2 text-sm text-gray-600">
          Manage your account security and authentication
        </p>
      </div>

      {success && (
        <div className="mb-6 flex items-center p-4 bg-green-50 rounded-md">
          <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2" />
          <span className="text-sm text-green-800">Security settings updated successfully!</span>
        </div>
      )}

      {/* Two-Factor Authentication */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <ShieldCheckIcon className="w-6 h-6 text-gray-400 mr-3" />
            <div>
              <h2 className="text-lg font-medium text-gray-900">Two-Factor Authentication</h2>
              <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
            </div>
          </div>
          <span
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              twoFactorEnabled
                ? 'bg-green-100 text-green-800'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {twoFactorEnabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
        <button
          onClick={handleToggle2FA}
          className={`w-full sm:w-auto px-4 py-2 rounded-md shadow-sm text-sm font-medium ${
            twoFactorEnabled
              ? 'border border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              : 'border-transparent text-white bg-indigo-600 hover:bg-indigo-700'
          }`}
        >
          {twoFactorEnabled ? 'Manage 2FA' : 'Enable 2FA'}
        </button>
      </div>

      {/* Password */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div className="flex items-center mb-4">
          <LockClosedIcon className="w-6 h-6 text-gray-400 mr-3" />
          <div>
            <h2 className="text-lg font-medium text-gray-900">Password</h2>
            <p className="text-sm text-gray-600">Update your password regularly to keep your account secure</p>
          </div>
        </div>

        {!showPasswordForm ? (
          <button
            onClick={() => setShowPasswordForm(true)}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Change Password
          </button>
        ) : (
          <form onSubmit={handlePasswordChange} className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Current Password</label>
              <input
                type="password"
                required
                value={passwords.current}
                onChange={(e) => setPasswords({ ...passwords, current: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">New Password</label>
              <input
                type="password"
                required
                minLength={8}
                value={passwords.new}
                onChange={(e) => setPasswords({ ...passwords, new: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-500">Minimum 8 characters</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
              <input
                type="password"
                required
                value={passwords.confirm}
                onChange={(e) => setPasswords({ ...passwords, confirm: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              />
            </div>
            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update Password'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowPasswordForm(false);
                  setPasswords({ current: '', new: '', confirm: '' });
                }}
                className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button
          onClick={() => navigate('/dashboard/settings/sessions')}
          className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-indigo-500 transition-colors"
        >
          <DevicePhoneMobileIcon className="w-8 h-8 text-indigo-600 mr-3" />
          <div className="text-left">
            <h3 className="text-sm font-medium text-gray-900">Active Sessions</h3>
            <p className="text-xs text-gray-600">Manage your devices</p>
          </div>
        </button>
        <button
          onClick={() => navigate('/dashboard/settings/login-history')}
          className="flex items-center p-4 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-indigo-500 transition-colors"
        >
          <KeyIcon className="w-8 h-8 text-indigo-600 mr-3" />
          <div className="text-left">
            <h3 className="text-sm font-medium text-gray-900">Login History</h3>
            <p className="text-xs text-gray-600">View recent activity</p>
          </div>
        </button>
      </div>

      {/* Security Recommendations */}
      <div className="mt-6 bg-yellow-50 rounded-lg border border-yellow-200 p-4">
        <div className="flex">
          <ExclamationTriangleIcon className="w-5 h-5 text-yellow-600 mr-2 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-medium text-yellow-800">Security Recommendations</h3>
            <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
              {!twoFactorEnabled && <li>Enable two-factor authentication for better security</li>}
              <li>Use a unique, strong password</li>
              <li>Review your active sessions regularly</li>
              <li>Keep your email address up to date</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecuritySettingsPage;
